import { AxiosError } from 'axios';
import {
  type Bar,
  type DatafeedErrorCallback,
  type HistoryCallback,
  type IBasicDataFeed,
  type LibrarySymbolInfo,
  type OnReadyCallback,
  type PeriodParams,
  type ResolutionString,
  type ResolveCallback,
  type SearchSymbolsCallback,
  type ServerTimeCallback,
  type SubscribeBarsCallback,
} from 'charting_library';

import { type IMediator } from '#/core/mediator';

import { type HistoryUrlParams } from '#/services/api/api.types';
import { type IConfigProviderService } from '#/services/config-provider';
import { type IUrlParamsService, UrlParamsName } from '#/services/url-params';

import { SystemError } from '#/shared/exceptions/system-error';
import { isTickTimeframe } from '#/shared/utils/is-tick-timeframe';
import { udfResponseToBarsResult } from '#/shared/utils/udf-response-to-bars-result';

import { type CandlesUdfResponse, type TicksUdfResponse } from '#/types/udf.types';

import { SERVICES_TYPES } from '#/services-types';

import { type IApiService } from '../api';
import { type IDeviceService } from '../device';
import { type IIntervalBarUpdateService } from '../interval-bar-update';
import { type ILogService } from '../log';
import { type IRealtimeBarUpdateService } from '../realtime-bar-update';
import { type ISentryService } from '../sentry';
import { type IServerTimeService } from '../server-time';
import { type IVisibilityService } from '../visibility';

import { type IDataFeedService } from './data-feed.types';

export class DataFeedService implements IDataFeedService, IBasicDataFeed {
  readonly #serviceName = 'DataFeedService';

  get #apiService() {
    return this._mediator.get<IApiService>(SERVICES_TYPES.ApiService);
  }
  get #deviceService() {
    return this._mediator.get<IDeviceService>(SERVICES_TYPES.DeviceService);
  }
  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }
  get #configProviderService() {
    return this._mediator.get<IConfigProviderService>(SERVICES_TYPES.ConfigProviderService);
  }
  get #intervalBarUpdateService() {
    return this._mediator.get<IIntervalBarUpdateService>(SERVICES_TYPES.IntervalBarUpdateService);
  }
  get #realtimeBarUpdateService() {
    return this._mediator.get<IRealtimeBarUpdateService>(SERVICES_TYPES.RealtimeBarUpdateService);
  }
  get #visibilityService() {
    return this._mediator.get<IVisibilityService>(SERVICES_TYPES.VisibilityService);
  }
  get #serverTimeService() {
    return this._mediator.get<IServerTimeService>(SERVICES_TYPES.ServerTimeService);
  }
  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }
  get #sentryService() {
    return this._mediator.get<ISentryService>(SERVICES_TYPES.SentryService);
  }

  #earliestBarTimeIsSecondsBySymbolAndTimeframe: {
    [symbolName: string]: {
      [timeframe: ResolutionString]: number | undefined;
    };
  } = {};

  #getSafeToRequestParameter = ({
    symbolName,
    resolution,
    isFirstDataRequest,
    from,
    to,
  }: {
    symbolName: string;
    resolution: ResolutionString;
    isFirstDataRequest: boolean;
    from: number;
    to: number;
  }) => {
    const earliestBarTime = this.#earliestBarTimeIsSecondsBySymbolAndTimeframe?.[symbolName]?.[resolution];

    if (isFirstDataRequest || !earliestBarTime) {
      return to;
    }

    if (from > earliestBarTime) {
      return to;
    }

    return earliestBarTime;
  };

  get #realtimeBarUpdateIsEnabled() {
    return Boolean(this.#deviceService.parentApp);
  }

  #symbolIsHandedOverToParentApp(symbolName: string) {
    if (this.#configProviderService.featureOptions?.['chart-templates'] !== true) return false;

    if (!this.#deviceService.parentApp) return false;

    const urlSymbol = this.#urlParamsService.getParamByName(UrlParamsName.Symbol)?.toUpperCase();

    return Boolean(urlSymbol) && symbolName.toUpperCase() !== urlSymbol;
  }

  #mtDataSource: string | undefined;

  constructor(private readonly _mediator: IMediator) {
    this.#logService.info(this.#serviceName, 'Instance was created.');
  }

  onReady(onReadyCallback: OnReadyCallback) {
    this.#mtDataSource = this.#urlParamsService.getParamByName(UrlParamsName.Ds) || undefined;

    // ? Handling not existent data source in URL only on start of the app
    if (!this.#mtDataSource) {
      this.#logService.warn(this.#serviceName, 'Missing "ds" parameter for history request in page URL.', {
        sourceParameterFromUrl: this.#mtDataSource,
      });
    }

    (async () => {
      try {
        const configResponse = await this.#apiService.config();

        this.#sentryService.captureBreadcrumb({
          category: 'chart.onReady',
          message: 'Config loaded.',
          level: 'info',
          data: { configKeys: Object.keys(configResponse.data) },
        });

        onReadyCallback(configResponse.data);
      } catch (onReadyError) {
        this.#sentryService.captureBreadcrumb({
          category: 'chart.onReady',
          message: 'Config failed.',
          level: 'error',
          data: {
            message: String(onReadyError),
            ...(onReadyError instanceof AxiosError && onReadyError.response
              ? { httpStatus: onReadyError.response.status, httpUrl: onReadyError.config?.url }
              : {}),
          },
        });

        this.#logService.error(this.#serviceName, new SystemError('Failed call onReady callback.', { onReadyError }));

        onReadyCallback({});
      }
    })();
  }

  async getServerTime(serverTimeCallback: ServerTimeCallback): Promise<void> {
    serverTimeCallback(this.#serverTimeService.now() / 1000);
  }

  async symbolIsUnavailable(symbolName: string): Promise<boolean> {
    try {
      const response = await this.#apiService.symbols({ urlParams: { symbol: symbolName } });
      const responseData = response.data;

      if (!responseData || 's' in responseData) return false;

      return !responseData.name;
    } catch (symbolIsUnavailableError) {
      this.#logService.warn(this.#serviceName, `Failed to check symbol ${symbolName}.`, { symbolIsUnavailableError });

      return false;
    }
  }

  async resolveSymbol(symbolName: string, onResult: ResolveCallback, onError: DatafeedErrorCallback) {
    // ? Fix incorrect symbol from library
    const symbol = symbolName.replace(':', '');

    try {
      const response = await this.#apiService.symbols({ urlParams: { symbol } });
      const responseData = response.data;

      if (!responseData) {
        throw new SystemError('No symbol info in resolveSymbol request.', { symbol: symbolName, responseData });
      }

      if ('s' in responseData) {
        throw new SystemError('Unknown error on resolveSymbol.', { symbol: symbolName, responseData });
      }

      if (!responseData.name) {
        throw new SystemError('Unavailable symbol in resolveSymbol request.', { symbol: symbolName, responseData });
      }

      const brandName = this.#configProviderService.brandName;

      const resolvedSymbolInfo: LibrarySymbolInfo = {
        ...responseData,
        // ? Fix for correct display drawings in chart
        session: '24x7',
        ...(brandName
          ? {
              exchange: brandName,
              listed_exchange: brandName,
            }
          : {}),
      };

      this.#sentryService.captureBreadcrumb({
        category: 'chart.resolveSymbol',
        message: 'Symbol resolved.',
        level: 'info',
        data: { symbol },
      });

      onResult(resolvedSymbolInfo);
    } catch (resolveSymbolError) {
      this.#sentryService.captureBreadcrumb({
        category: 'chart.resolveSymbol',
        message: 'Symbol resolution failed.',
        level: 'error',
        data: {
          symbol,
          message: String(resolveSymbolError),
          ...(resolveSymbolError instanceof AxiosError && resolveSymbolError.response
            ? { httpStatus: resolveSymbolError.response.status, httpUrl: resolveSymbolError.config?.url }
            : {}),
        },
      });

      this.#logService.error(
        this.#serviceName,
        new SystemError('Failed call resolveSymbol callback.', { resolveSymbolError })
      );

      if (resolveSymbolError instanceof SystemError) {
        onError(resolveSymbolError.message);

        return;
      }

      onError('Unknown resolve symbol error.');
    }
  }

  async getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: DatafeedErrorCallback
  ) {
    if (this.#symbolIsHandedOverToParentApp(symbolInfo.name)) return;

    if (!this.#earliestBarTimeIsSecondsBySymbolAndTimeframe[symbolInfo.name]) {
      this.#earliestBarTimeIsSecondsBySymbolAndTimeframe[symbolInfo.name] = {};
    }

    try {
      let udfResponse: CandlesUdfResponse | TicksUdfResponse;

      if (isTickTimeframe(resolution)) {
        const from = periodParams.from * 1000; // Should be milliseconds timestamp,
        const to =
          this.#getSafeToRequestParameter({
            symbolName: symbolInfo.name,
            resolution,
            isFirstDataRequest: periodParams.firstDataRequest,
            from,
            to: periodParams.to,
          }) * 1000; // Should be milliseconds timestamp,

        const urlParams: HistoryUrlParams = {
          symbol: symbolInfo.name,
          from,
          to,
          countback: periodParams.countBack,
          resolution,
          source: this.#mtDataSource,
        };

        udfResponse = (await this.#apiService.ticksHistory({ urlParams })).data;
      } else {
        const from = periodParams.from; // Should be seconds timestamp
        const to = this.#getSafeToRequestParameter({
          symbolName: symbolInfo.name,
          resolution,
          isFirstDataRequest: periodParams.firstDataRequest,
          from,
          to: periodParams.to,
        }); // Should be seconds timestamp

        const urlParams: HistoryUrlParams = {
          symbol: symbolInfo.name,
          from,
          to,
          resolution,
          countback: periodParams.countBack,
          source: this.#mtDataSource,
        };

        udfResponse = (await this.#apiService.candlesHistory({ urlParams })).data;
      }

      const { bars, meta } = udfResponseToBarsResult(udfResponse);

      this.#sentryService.captureBreadcrumb({
        category: 'chart.getBars',
        message: 'Bars loaded.',
        level: 'info',
        data: {
          symbol: symbolInfo.name,
          resolution,
          from: periodParams.from,
          to: periodParams.to,
          barsCount: bars.length,
        },
      });

      const earliestBar = bars.at(0);

      if (earliestBar) {
        this.#earliestBarTimeIsSecondsBySymbolAndTimeframe[symbolInfo.name][resolution] = earliestBar.time / 1000;
      }

      if (bars.length > 0 && periodParams.firstDataRequest) {
        if (this.#realtimeBarUpdateIsEnabled) {
          const lastBar = bars.at(-1) as Bar;

          this.#logService.info(
            this.#serviceName,
            'First request. Run realtime bar update service with last bar:',
            lastBar
          );

          this.#realtimeBarUpdateService.setLastBarCache(`${symbolInfo.name}:${resolution}`, lastBar);
        } else {
          this.#logService.info(this.#serviceName, 'First request. Run interval bar update service.');

          this.#intervalBarUpdateService.run();
        }
      }

      onResult(bars, meta);
    } catch (getBarsError) {
      this.#sentryService.captureBreadcrumb({
        category: 'chart.getBars',
        message: 'Bars load failed.',
        level: 'error',
        data: {
          symbol: symbolInfo.name,
          resolution,
          from: periodParams.from,
          to: periodParams.to,
          message: String(getBarsError),
          ...(getBarsError instanceof AxiosError && getBarsError.response
            ? { httpStatus: getBarsError.response.status, httpUrl: getBarsError.config?.url }
            : {}),
        },
      });

      this.#logService.error(
        this.#serviceName,
        new SystemError('Failed call getBars callback.', { getBarsError, periodParams })
      );

      if (getBarsError instanceof SystemError) {
        onError(getBarsError.message);

        return;
      }

      onError('Unknown getBars error.');
    }
  }

  subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    listenerGuid: string,
    onResetCacheNeededCallback: () => void
  ) {
    if (this.#realtimeBarUpdateIsEnabled) {
      this.#realtimeBarUpdateService.subscribe({
        symbolInfo,
        resolution,
        newDataCallback: onTick,
      });
    } else {
      this.#intervalBarUpdateService.subscribe({
        listenerGuid,
        symbolInfo,
        resolution,
        newDataCallback: onTick,
      });
    }

    this.#visibilityService.resetCache = onResetCacheNeededCallback;
  }

  unsubscribeBars(listenerGuid: string) {
    if (this.#realtimeBarUpdateIsEnabled) {
      this.#realtimeBarUpdateService.unsubscribe(listenerGuid);
    } else {
      this.#intervalBarUpdateService.unsubscribe(listenerGuid);
    }
  }

  // @todo Implement the search itself when the backend exposes a symbols search endpoint.
  // ? Until then the callback must still be called: the library shows an endless loader while it waits for it.
  // ? `allow_arbitrary_symbol_search_input` lets the typed symbol reach `resolveSymbol` without search results.
  async searchSymbols(userInput: string, exchange: string, symbolType: string, onResult: SearchSymbolsCallback) {
    this.#logService.info(this.#serviceName, 'searchSymbols call', { userInput, exchange, symbolType, onResult });

    onResult([]);
  }
}
